import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * How far one tile's centre is from the middle of the viewport, in px.
 *
 * Measured at PUBLISH time, not read off the IntersectionObserver entry. The
 * entry carries `boundingClientRect` for free, which is tempting, but an
 * observer only fires when a threshold is crossed: a tile that stays partially
 * visible through a scroll never re-reports, so its recorded position would be
 * where it was when it ENTERED — always near an edge. Every tile would then
 * carry a similar stale distance and the ordering would be arbitrary again,
 * which is the bug this is meant to fix.
 *
 * The cost is one layout flush per publish (debounced to at most ~5/sec) over
 * the tiles currently on screen, which is the right trade for an order that is
 * actually true.
 *
 * The observer has no `root`, so its root IS the viewport.
 */
function distanceFromViewportCentre(el: Element): number {
  if (typeof window === 'undefined' || !el?.getBoundingClientRect) return 0;
  const box = el.getBoundingClientRect();
  const dx = box.left + box.width / 2 - window.innerWidth / 2;
  const dy = box.top + box.height / 2 - window.innerHeight / 2;
  // Vertical distance dominates in every grid this serves, but the hypotenuse
  // costs nothing and behaves sanely on a wide multi-column layout too.
  return Math.hypot(dx, dy);
}

/**
 * "Which of these things can the user actually see?" — one IntersectionObserver
 * for a whole grid, reported as a debounced set of keys.
 *
 * Generalised from the dashboard's cover warming, which is where the three
 * non-obvious bits were each paid for once already:
 *
 * 1. **The element→key Map is the source of truth, not the observer.** React
 *    attaches refs during commit and runs effects afterwards, so on first mount
 *    every tile registers BEFORE the observer exists. The observer therefore has
 *    to adopt whatever is already in the map when it is created.
 * 2. **Ref callbacks are cached per key.** Handing React a new function identity
 *    on every render makes it detach and reattach every ref, which churns the
 *    observer on a grid that re-renders whenever a thumbnail lands.
 * 3. **Detach has to unobserve.** Otherwise the map grows to hold every tile the
 *    page has ever rendered, and stale nodes keep reporting.
 *
 * The published order is NEAREST FIRST, by distance from the root's centre.
 * That is not cosmetic. A consumer caps what it asks for (`useThumbWarm` slices
 * to 24, the server rejects more), so on a tall grid the order decides WHICH
 * tiles get rendered at all in this round. This used to publish
 * `[...keys].sort()` — lexicographic on `slug\0id` — so a truncated batch kept
 * whichever 24 ids sorted first, which is unrelated to where the user is
 * looking, and tiles filled in alphabetical order rather than outward from the
 * cursor. The server's own comment on POST /thumbs/visible already claimed this
 * ordering existed; now it does.
 *
 * `visible` keeps a stable identity while its contents are unchanged, so it is
 * safe in an effect's dependency list.
 */
export type VisibleSetOptions = {
  /** false disconnects and reports nothing. */
  enabled?: boolean;
  /** How far ahead of the fold to look, so an image is often already there by
   *  the time the tile is looked at. */
  rootMargin?: string;
  /** A fast scroll must not fire a request per frame; this is how long the
   *  viewport has to settle. */
  debounceMs?: number;
};

/** The ref callback a tile attaches. Typed as React's own ref shape so it can
 *  be passed straight to `ref=`. */
export type TileRef = (el: Element | null) => void;

export function useVisibleSet({ enabled = true, rootMargin = '200px', debounceMs = 200 }: VisibleSetOptions = {}): {
  visible: string[];
  observe: (key: string) => TileRef;
} {
  const [visible, setVisible] = useState<string[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elsRef = useRef(new Map<Element, string>()); // element → key
  const refsRef = useRef(new Map<string, TileRef>()); // key → ref callback
  const seenRef = useRef(new Set<string>()); // keys currently intersecting
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Publish the intersecting set, debounced, and only when it actually changed —
  // a new array identity every scroll tick would restart every consumer effect.
  //
  // Sorted nearest-first (see the note above), with the key as the tie-break so
  // the order is deterministic: two tiles equidistant either side of the centre
  // must not swap between publishes, or the identity check below would report a
  // change on every settle and restart the consumer's effect for nothing.
  const publish = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      // Walk the element→key map rather than the key set, because measuring
      // needs the element. It holds only mounted tiles, and we measure only the
      // ones on screen.
      const ranked: [string, number][] = [];
      for (const [el, key] of elsRef.current) {
        if (seenRef.current.has(key)) ranked.push([key, distanceFromViewportCentre(el)]);
      }
      ranked.sort((a, b) => a[1] - b[1] || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
      const next = ranked.map(([key]) => key);
      setVisible((cur) => (cur.length === next.length && cur.every((k, i) => k === next[i]) ? cur : next));
    }, debounceMs);
  }, [debounceMs]);

  useEffect(() => {
    if (!enabled) {
      seenRef.current.clear();
      setVisible((cur) => (cur.length === 0 ? cur : []));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const key = elsRef.current.get(entry.target);
          if (!key) continue;
          if (entry.isIntersecting) seenRef.current.add(key);
          else seenRef.current.delete(key);
        }
        publish();
      },
      { rootMargin },
    );
    observerRef.current = observer;
    for (const el of elsRef.current.keys()) observer.observe(el);
    return () => {
      observer.disconnect();
      observerRef.current = null;
      clearTimeout(timerRef.current);
    };
  }, [enabled, rootMargin, publish]);

  const observe = useCallback(
    (key: string): TileRef => {
      let fn = refsRef.current.get(key);
      if (!fn) {
        fn = (el: Element | null) => {
          if (el) {
            elsRef.current.set(el, key);
            observerRef.current?.observe(el);
            return;
          }
          // Unmounted — forget it, or the map holds every tile ever rendered and
          // a scrolled-away key stays "visible" forever.
          for (const [node, k] of elsRef.current) {
            if (k === key) {
              observerRef.current?.unobserve(node);
              elsRef.current.delete(node);
            }
          }
          if (seenRef.current.delete(key)) publish();
        };
        refsRef.current.set(key, fn);
      }
      return fn;
    },
    [publish],
  );

  return { visible, observe };
}
